import React, { useState } from "react";
import { Box, Image, Text, Tooltip, Skeleton , HStack, Switch} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { getImageURL } from "../../util/getImage";

const IMAGE_BASE_PATH = process.env.REACT_APP_DEV_IMAGE_BASE_PATH;
const placeholderImg = `${IMAGE_BASE_PATH}/ui/DummyImage/small.png`;

export const GridCard = ({
  record,
  onClick,
  href,
  onToggleVisible,
  selectable,
  isSelected,
  onToggleSelect,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();
  const imageUrl = record?.direct_url_of_preview_image || record?.direct_url_of_cover_image;
  const title = record?.title || "Untitled";

  // When an href is provided, render the card as a real <a href> anchor so native
  // open-in-new-tab works (right-click menu, Ctrl/Cmd+click, middle click). A plain
  // left-click is intercepted and routed through SPA navigation; modified/middle
  // clicks fall through to the browser's default new-tab/window behaviour.
  // Without href we keep the original onClick behaviour so other consumers of this
  // shared component are unaffected.
  const handleAnchorClick = (e) => {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    navigate(href);
  };

  const linkProps = href
    ? {
        as: "a",
        href,
        onClick: handleAnchorClick,
        _hover: { textDecoration: "none" },
        // Opt out of the app-wide right-click suppression (see App.js) so the
        // browser's "Open link in new tab/window" menu is available on these links.
        "data-allow-context-menu": "true",
      }
    : { onClick };

  const handleToggle = (e) => {
    e.stopPropagation();
    if (onToggleVisible) {
      onToggleVisible(record, !record.isVisible);
    }
  };

  return (
    <Box
      bg="white"
      overflow="hidden"
      boxShadow="sm"
      border="2px solid"
      borderColor={selectable && isSelected ? "blue.400" : "gray.200"}
      cursor="pointer"
      display="flex"
      flexDirection="column"
      height="100%"
      position="relative"
      {...linkProps}
    >
      {selectable && (
        <Box
          position="absolute"
          top={2}
          left={2}
          zIndex={2}
          onClick={(e) => {
            // preventDefault here would also cancel a native checkbox's own
            // toggle (same click, same default-action resolution), so this
            // is a fully custom indicator driven only by this click handler
            // rather than a native input + onChange.
            e.preventDefault();
            e.stopPropagation();
            onToggleSelect && onToggleSelect(!isSelected);
          }}
        >
          <Box
            width="22px"
            height="22px"
            borderRadius="full"
            border="2px solid"
            borderColor={isSelected ? "blue.500" : "white"}
            bg={isSelected ? "blue.500" : "whiteAlpha.800"}
            boxShadow="0 0 0 1px rgba(0,0,0,0.15)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            transition="background 0.15s, border-color 0.15s"
          >
            {isSelected && <CheckIcon color="white" boxSize={2.5} />}
          </Box>
        </Box>
      )}
      <Box position="relative" height="200px" bg="#f0f0f0">
        <Skeleton isLoaded={imageLoaded} width="100%" height="100%">
          <Image
            src={getImageURL(imageUrl, "400", "300")}
            alt={title}
            fallbackSrc={placeholderImg}
            width="100%"
            height="100%"
            objectFit="contain"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageLoaded(true)}
          />
        </Skeleton>
      </Box>
      <Box p={4} flex="1">
        <Tooltip label={title} placement="top" hasArrow>
          <Text
            fontSize="md"
            fontWeight="600"
            color="gray.800"
            noOfLines={2}
            lineHeight="short"
          >
            {title}
          </Text>
        </Tooltip>
        {record?.sortable_year && (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {record.sortable_year}
          </Text>
        )}
        <HStack mt={2} spacing={2} onClick={(e) => e.stopPropagation()}>
          <Text fontSize="xs" color="gray.500" fontWeight="500">
            Is Visible:
          </Text>
          <Switch
            size="sm"
            colorScheme="green"
            isChecked={!!record.isVisible}
            onChange={handleToggle}
          />
          <Text fontSize="xs" color={record.isVisible ? "green.600" : "red.500"} fontWeight="600">
            {record.isVisible ? "Yes" : "No"}
          </Text>
        </HStack>
      </Box>
    </Box>
  );
};
